import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { ArrowRight, CheckCircle2, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';

const DEFAULT_HEADLINE = 'Get your free AI ad creative audit in 48 hours';
const DEFAULT_SUBHEADLINE =
  'Drop your email and we will ship a tailored teardown of your current ad creative — what is working, what is not, and 3 AI-native tests to run next week.';

const FALLBACK_EMAIL = 'hello@cultiv-ai.studio';

const leadSchema = z.object({
  email: z
    .string()
    .trim()
    .min(1, { message: 'Email is required' })
    .email({ message: 'Enter a valid email address' }),
  website: z
    .string()
    .trim()
    .max(200, { message: 'Website URL is too long' })
    .optional()
    .or(z.literal('')),
  // Honeypot — real humans never fill this; bots do.
  company: z
    .string()
    .max(0, { message: 'Spam detected' })
    .optional()
    .or(z.literal('')),
});

const resolveLeadsEndpoint = () => {
  const base = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '');
  return base ? `${base}/api/leads` : '/api/leads';
};

const buildMailtoFallback = ({ email, website }) => {
  const subject = encodeURIComponent('Free AI ad creative audit request');
  const lines = [
    `Email: ${email}`,
    website ? `Website: ${website}` : null,
    '',
    'Please send my free AI ad creative audit.',
  ].filter(Boolean);
  const body = encodeURIComponent(lines.join('\n'));
  return `mailto:${FALLBACK_EMAIL}?subject=${subject}&body=${body}`;
};

/**
 * LeadCaptureForm — "Free AI Ad Creative Audit" lead magnet.
 *
 * @param {Object}   props
 * @param {'inline'|'card'} [props.variant='inline']
 * @param {string}   [props.headline]
 * @param {string}   [props.subheadline]
 * @param {string}   [props.ctaLabel]
 * @param {function} [props.onSuccess] Fires after successful submit with the form values
 * @param {string}   [props.className]
 */
const LeadCaptureForm = ({
  variant = 'inline',
  headline = DEFAULT_HEADLINE,
  subheadline = DEFAULT_SUBHEADLINE,
  ctaLabel = 'Get my free audit',
  onSuccess,
  className,
}) => {
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: zodResolver(leadSchema),
    defaultValues: { email: '', website: '', company: '' },
    mode: 'onBlur',
  });

  const [status, setStatus] = useState('idle');
  const [errorMessage, setErrorMessage] = useState('');

  const onSubmit = async (values) => {
    setErrorMessage('');
    setStatus('submitting');
    const payload = {
      email: values.email,
      website: values.website || null,
      source: 'ai-creative-audit',
      lead_magnet: 'ai-ad-creative-audit-48h',
    };

    try {
      const endpoint = resolveLeadsEndpoint();
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!response.ok) {
        throw new Error(`Request failed with status ${response.status}`);
      }
      setStatus('success');
      reset();
      if (typeof onSuccess === 'function') onSuccess(values);
    } catch (err) {
      if (typeof window !== 'undefined') {
        window.location.href = buildMailtoFallback(values);
      }
      setStatus('error');
      setErrorMessage(
        'We could not reach our lead inbox. Your email client should open so you can send us the request directly.',
      );
    }
  };

  const isInline = variant === 'inline';
  const isCard = variant === 'card';

  if (status === 'success') {
    return (
      <div
        data-testid="lead-capture-success"
        className={cn(
          'rounded-2xl border border-emerald-400/30 bg-emerald-400/10 text-white',
          isCard ? 'p-8 md:p-10' : 'p-6',
          className,
        )}
        role="status"
        aria-live="polite"
      >
        <div className="flex items-start gap-3">
          <CheckCircle2 className="h-6 w-6 text-emerald-300 mt-1 shrink-0" />
          <div>
            <p className="text-lg md:text-xl font-medium mb-1">
              Thanks — your audit is queued.
            </p>
            <p className="text-sm md:text-base text-white/70">
              A senior creative director at Cultiv AI Studio will review your
              current ad creative and reply within 48 hours with a written
              teardown plus three AI-native tests to run next.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <form
      noValidate
      onSubmit={handleSubmit(onSubmit)}
      data-testid="lead-capture-form"
      className={cn(
        'text-white',
        isCard &&
          'rounded-2xl border border-white/10 bg-white/[0.03] backdrop-blur-sm p-6 md:p-10',
        className,
      )}
      aria-label="Free AI ad creative audit signup"
    >
      {isCard && (headline || subheadline) ? (
        <div className="mb-6 md:mb-8">
          {headline ? (
            <h3 className="text-2xl md:text-3xl font-light tracking-tight mb-3">
              {headline}
            </h3>
          ) : null}
          {subheadline ? (
            <p className="text-sm md:text-base text-white/60 leading-relaxed">
              {subheadline}
            </p>
          ) : null}
        </div>
      ) : null}

      {/* Honeypot — visually hidden from real users */}
      <div
        aria-hidden="true"
        className="absolute left-[-10000px] top-auto w-px h-px overflow-hidden"
      >
        <label htmlFor="lead-company">Company (leave blank)</label>
        <input
          id="lead-company"
          type="text"
          tabIndex={-1}
          autoComplete="off"
          {...register('company')}
        />
      </div>

      <div
        className={cn(
          isInline
            ? 'flex flex-col sm:flex-row gap-3'
            : 'flex flex-col gap-4',
        )}
      >
        <div className={cn('flex-1', isCard && 'space-y-2')}>
          <label
            htmlFor="lead-email"
            className={cn(
              'block text-sm font-medium text-white/80',
              isInline && 'sr-only',
              isCard && 'mb-1',
            )}
          >
            Work email
          </label>
          <Input
            id="lead-email"
            type="email"
            inputMode="email"
            autoComplete="email"
            placeholder="you@company.com"
            aria-invalid={errors.email ? 'true' : 'false'}
            aria-describedby={errors.email ? 'lead-email-error' : undefined}
            data-testid="lead-capture-email"
            className={cn(
              'bg-white/5 border-white/15 text-white placeholder:text-white/40',
              'focus-visible:ring-white/40 focus-visible:border-white/40',
              isCard ? 'h-11' : 'h-10',
            )}
            {...register('email')}
          />
          {errors.email ? (
            <p
              id="lead-email-error"
              data-testid="lead-capture-email-error"
              className="mt-2 text-xs text-red-300"
            >
              {errors.email.message}
            </p>
          ) : null}
        </div>

        {isCard ? (
          <div className="space-y-2">
            <label
              htmlFor="lead-website"
              className="block text-sm font-medium text-white/80 mb-1"
            >
              Website <span className="text-white/40">(optional)</span>
            </label>
            <Input
              id="lead-website"
              type="url"
              autoComplete="url"
              placeholder="https://yourbrand.com"
              aria-invalid={errors.website ? 'true' : 'false'}
              aria-describedby={errors.website ? 'lead-website-error' : undefined}
              data-testid="lead-capture-website"
              className="h-11 bg-white/5 border-white/15 text-white placeholder:text-white/40 focus-visible:ring-white/40 focus-visible:border-white/40"
              {...register('website')}
            />
            {errors.website ? (
              <p
                id="lead-website-error"
                className="mt-2 text-xs text-red-300"
              >
                {errors.website.message}
              </p>
            ) : null}
          </div>
        ) : null}

        <Button
          type="submit"
          size={isCard ? 'lg' : 'default'}
          disabled={isSubmitting || status === 'submitting'}
          data-testid="lead-capture-submit"
          className={cn(
            'bg-white text-black hover:bg-white/90 font-medium',
            isInline ? 'sm:w-auto w-full' : 'w-full',
          )}
        >
          {status === 'submitting' || isSubmitting ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
              <span>Sending</span>
            </>
          ) : (
            <>
              <span>{ctaLabel}</span>
              <ArrowRight className="h-4 w-4" aria-hidden="true" />
            </>
          )}
        </Button>
      </div>

      {status === 'error' && errorMessage ? (
        <p
          role="alert"
          data-testid="lead-capture-error"
          className="mt-4 text-xs text-amber-200/90"
        >
          {errorMessage}
        </p>
      ) : null}

      {isInline ? (
        <p className="mt-3 text-xs text-white/40">
          No spam. Unsubscribe any time.
        </p>
      ) : null}
    </form>
  );
};

export default LeadCaptureForm;
